import * as THREE from "three";

export type Measurement3DLabelScaleOptions = {
  worldHeight: number;
  maxScreenHeightPx: number;
  minScale?: number;
  maxScale?: number;
};

export function getMeasurement3DLabelScale(
  camera: THREE.Camera,
  labelPosition: THREE.Vector3,
  viewportHeightPx: number,
  options: Measurement3DLabelScaleOptions,
): number {
  const worldUnitsPerPixel = getWorldUnitsPerPixelAtPoint(
    camera,
    labelPosition,
    viewportHeightPx,
  );

  if (
    worldUnitsPerPixel === null ||
    !Number.isFinite(worldUnitsPerPixel) ||
    worldUnitsPerPixel <= 0 ||
    !Number.isFinite(options.worldHeight) ||
    options.worldHeight <= 0 ||
    !Number.isFinite(options.maxScreenHeightPx) ||
    options.maxScreenHeightPx <= 0
  ) {
    return 1;
  }

  const currentScreenHeightPx = options.worldHeight / worldUnitsPerPixel;
  if (
    !Number.isFinite(currentScreenHeightPx) ||
    currentScreenHeightPx <= options.maxScreenHeightPx
  ) {
    return 1;
  }

  return THREE.MathUtils.clamp(
    options.maxScreenHeightPx / currentScreenHeightPx,
    options.minScale ?? 0,
    options.maxScale ?? 1,
  );
}

export function getWorldUnitsPerPixelAtPoint(
  camera: THREE.Camera,
  point: THREE.Vector3,
  viewportHeightPx: number,
): number | null {
  if (!Number.isFinite(viewportHeightPx) || viewportHeightPx <= 0) {
    return null;
  }

  camera.updateMatrixWorld();

  if (camera instanceof THREE.PerspectiveCamera) {
    const cameraSpacePoint = point.clone().applyMatrix4(camera.matrixWorldInverse);
    const depth = Math.abs(cameraSpacePoint.z);
    if (!Number.isFinite(depth) || depth <= 1e-6) {
      return null;
    }

    const visibleHeight =
      (2 * depth * Math.tan(THREE.MathUtils.degToRad(camera.fov) / 2)) /
      Math.max(camera.zoom, 1e-6);

    return visibleHeight / viewportHeightPx;
  }

  if (camera instanceof THREE.OrthographicCamera) {
    const visibleHeight =
      (camera.top - camera.bottom) / Math.max(camera.zoom, 1e-6);
    return visibleHeight / viewportHeightPx;
  }

  return null;
}
